import React, { useState, FC, useEffect } from 'react'
import { message, Modal, Switch, Table, Button } from 'antd'
import { ColumnProps } from 'antd/lib/table'

import { Role, UserInviteFormValues, UserListItem, UserUpdateFormValues, AuthenticatedUserOrganization, User, UserFormValues } from '../../types'
import { inviteService, userService } from '../../services'
import { SearchBar, Page, EditUserModal } from '../../components'
import CreateInviteModal from '../Invites/CreateInviteModal'
import styles from './index.module.less'
import RequestError from '../../services/RequestError'
import EditResellerUserModal from './EditResellerUserModal'
import config from '../../config'

const { confirm } = Modal

interface EditUserProps {
	user: UserListItem
	getUsers: () => Promise<void>
}
const EditUserButton: React.FC<EditUserProps> = ({ user, getUsers }) => {
	const [isVisible, setIsVisible] = useState(false)
	const belongsToOrganization = Boolean(user.organizations?.length)
	const openForm = () => setIsVisible(true)
	const closeForm = () => setIsVisible(false)
	const updateSpUser = async (values: UserFormValues) => {
		try {
			await userService.update(user.id, { service_provider_ids: values.service_provider_ids })
			setIsVisible(false)
			message.success('Updated user')
			await getUsers()
		} catch (error) {
			message.error(`Error updating "${user.email}"`)
		}
	}
	const updateResellerUser = async (values: UserFormValues) => {
		try {
			await userService.update(user.id, { reseller_id: values.reseller_id })
			setIsVisible(false)
			message.success('Updated user')
			await getUsers()
		} catch (error) {
			message.error(`Error updating "${user.email}"`)
		}
	}
	return belongsToOrganization ? (
		<>
			<Button onClick={openForm} className={styles.name} icon='edit' type='link'>
				{user.name}
			</Button>

			{user.organization_type === 'reseller' ? (
				<EditResellerUserModal user={user as User} isVisible={isVisible} close={closeForm} update={updateResellerUser} />
			) : null}
			{user.organization_type === 'service_provider' ? (
				<EditUserModal user={user as User} isVisible={isVisible} close={closeForm} update={updateSpUser} />
			) : null}
		</>
	) : (
		<span>{user.name}</span>
	)
}

const Users: FC = () => {
	const [loading, setLoading] = useState<boolean>(false)
	const [users, setUsers] = useState<UserListItem[]>([])
	const [filter, setFilter] = useState<string>()
	const [isCreateInviteModalVisible, setIsCreateInviteModalVisible] = useState<boolean>(false)

	const searchBarButtonProps = () => ({
		onClick: (): void => setIsCreateInviteModalVisible(true),
		children: 'Invite user',
		icon: 'plus'
	})

	const getUsers = async () => {
		try {
			const result = await userService.getAll()
			setUsers(result.data)
		} catch (error) {
			message.error('Something went wrong when attempting to fetch the users. Refresh the page to try again.')
		}
	}

	const deleteUser = async ({ id, name }: UserListItem) => {
		setLoading(true)

		try {
			await userService.delete(id)
			await getUsers()
			message.success(`${name} has been successfully deleted`)
		} catch (error) {
			message.error('Something went wrong when attempting to delete this user. Please try again.')
		} finally {
			setLoading(false)
		}
	}

	const toggleUserIsEnabled = async (user: UserListItem) => {
		setLoading(true)

		try {
			const values: UserUpdateFormValues = { disabled: !user.disabled }
			await userService.update(user.id, values)
			await getUsers()
			const status = user.disabled ? 'activated' : 'disabled'
			message.success(`${user.name} has been successfully ${status}`)
		} catch (error) {
			const status = user.disabled ? 'activate' : 'disable'
			message.error(`Something went wrong when attempting to ${status} this user. Please try again.`)
		} finally {
			setLoading(false)
		}
	}

	const toggleUserCanAccessPublicApi = async (user: UserListItem) => {
		setLoading(true)
		try {
			const values: UserUpdateFormValues = { api_enabled: !user.api_enabled }
			await userService.update(user.id, values)
			await getUsers()
			const status = user.api_enabled ? 'disabled' : 'enabled'
			message.success(`Public API access for ${user.name} has been successfully ${status}`)
		} catch (error) {
			const status = user.api_enabled ? 'disable' : 'enable'
			message.error(`Something went wrong when attempting to ${status} public API access for this user. Please try again.`)
		} finally {
			setLoading(false)
		}
	}

	const columns: ColumnProps<UserListItem>[] = [
		{
			title: 'Name',
			dataIndex: 'name',
			sorter: (a: UserListItem, b: UserListItem) => a.name.localeCompare(b.name),
			render: (_: string, user) => <EditUserButton user={user} getUsers={getUsers} key={user.id} />
		},
		{ title: 'Email', dataIndex: 'email' },
		{
			title: 'Organization(s)',
			dataIndex: 'organizations',
			render: (orgs: AuthenticatedUserOrganization[]) => orgs.map(org => org.name).join(', ') || 'N/A',
			sorter: (a: UserListItem, b: UserListItem) => {
				const aName = a.organizations.map((org: AuthenticatedUserOrganization) => org.name).join(', ')
				const bName = b.organizations.map((org: AuthenticatedUserOrganization) => org.name).join(', ')
				if (!aName) {
					return -1
				}
				if (!bName) {
					return 1
				}
				return aName.localeCompare(bName)
			}
		},
		{
			title: 'Roles',
			dataIndex: 'roles',
			render: (roles: Role[]) => roles.map(r => r.name).join(', '),
			filters: [
				{ text: 'Mondago Administrator', value: 'Mondago Administrator' },
				{ text: 'Service Provider Administrator', value: 'Service Provider Administrator' },
				{ text: 'Reseller Administrator', value: 'Reseller Administrator' }
			],
			onFilter: (value: string, record: UserListItem) => record.roles.some(role => role.name === value)
		},
		{
			title: 'Enabled',
			width: 110,
			dataIndex: 'disabled',
			sorter: (a: UserListItem, b: UserListItem) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1),
			render: (_: string, user: UserListItem) => (
				<div style={{ textAlign: 'center' }}>
					<Switch
						title='Enable/Disable user'
						onChange={() => {
							const statusText = user.disabled ? 'Enable' : 'Disable'
							const content = user.disabled
								? 'This will allow the user to access the portal'
								: 'This will prevent the user from accessing the portal'

							confirm({
								title: `Are you sure you want to ${statusText.toLowerCase()} this user?`,
								content,
								okType: user.disabled ? 'primary' : 'danger',
								okText: `${statusText} User`,
								onOk() {
									toggleUserIsEnabled(user)
								}
							})
						}}
						loading={loading}
						checked={!user.disabled}
					/>
				</div>
			)
		},
		{
			title: '',
			width: 160,
			dataIndex: 'actions',
			render: (_: string, user: UserListItem) => (
				<Button
					type='danger'
					icon='delete'
					key='delete'
					onClick={() =>
						confirm({
							title: 'Are you sure you want to delete this user?',
							content: 'This will remove the user entirely from the active directory',
							okType: 'danger',
							okText: 'Delete',
							async onOk() {
								await deleteUser(user)
							}
						})
					}
				>
					Delete User
				</Button>
			)
		}
	]

	if (config.isPublicAPIManagementEnabled()) {
		columns.splice(5, 0, {
			title: 'Public API Access',
			width: 200,
			align: 'center',
			dataIndex: 'api_enabled',
			sorter: (a: UserListItem, b: UserListItem) => (a.api_enabled === b.api_enabled ? 0 : a.api_enabled ? -1 : 1),
			render: (_: string, user: UserListItem) => (
				<div style={{ textAlign: 'center' }}>
					<Switch
						title={`Enable/Disable API access for the user`}
						onChange={() => {
							const statusText = user.api_enabled ? 'Disable' : 'Enable'
							const content = user.api_enabled
								? 'This will prevent the user from accessing the public API'
								: 'This will allow the user to access the public API'

							confirm({
								title: `Are you sure you want to ${statusText.toLowerCase()} public API access for this user?`,
								content,
								okType: user.api_enabled ? 'danger' : 'primary',
								okText: `${statusText} public API access`,
								onOk() {
									toggleUserCanAccessPublicApi(user)
								}
							})
						}}
						loading={loading}
						checked={user.api_enabled}
					/>
				</div>
			)
		})
	}

	const createUserInvite = async (values: UserInviteFormValues): Promise<void> => {
		try {
			await inviteService.create(values)
			setIsCreateInviteModalVisible(false)
		} catch (e) {
			const error = e as RequestError
			if (error.response?.status === 409) {
				message.warning(`${values.email} has already been invited.`)
			} else {
				message.error('Something went wrong when trying to send the invitation. Please try again.')
			}
		}
	}

	useEffect(() => {
		const initUsers = async (): Promise<void> => {
			setLoading(true)
			await getUsers()
			setLoading(false)
		}

		initUsers()
	}, [])

	return (
		<Page title='Users'>
			<SearchBar className={styles.filter} placeholder='Filter by name' search={setFilter} buttonProps={searchBarButtonProps()} />
			<Table
				loading={loading}
				dataSource={users.filter(user => (filter ? user.name.toLowerCase().includes(filter.toLowerCase()) : true))}
				rowKey='id'
				columns={columns}
			/>
			<CreateInviteModal isVisible={isCreateInviteModalVisible} create={createUserInvite} close={() => setIsCreateInviteModalVisible(false)} />
		</Page>
	)
}

export default Users
